import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { Ticket } from 'src/app/models';

/**
 * Card de chamado no visual do v3ndor, para o histórico de apontamentos. Sem dependências
 * injetadas: recebe um `Ticket` e desenha.
 *
 * É uma CÓPIA do `#cardTpl` da sprint, não um reúso: aquele é um `ng-template` dentro de
 * `sprint-board.component.ts`, sem `<ng-content>`, e extraí-lo de lá exigiria religar
 * drag-and-drop, buckets e modo manutenção de `/v3ndor/sprint` — tela que esta entrega não pode
 * alterar. O `<ng-content>` daqui é o motivo de existir: a tela projeta dentro do card o total do
 * dia, o botão de expandir e a lista de apontamentos.
 */
@Component({
  selector: 'app-ticket-log-card',
  standalone: true,
  templateUrl: './ticket-log-card.component.html',
  styleUrls: ['./ticket-log-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketLogCardComponent {
  @Input({ required: true }) ticket!: Ticket;

  @Output() cardClick = new EventEmitter<MouseEvent>();

  /** O card só avisa; quem abre o menu é a tela, com uma instância só para o quadro inteiro. */
  @Output() contextMenuRequest = new EventEmitter<MouseEvent>();

  onContextMenu(event: MouseEvent): void {
    // `stopPropagation` evita que o evento suba e dispare o clique do card, que abriria o modal.
    event.preventDefault();
    event.stopPropagation();
    this.contextMenuRequest.emit(event);
  }

  get status(): string {
    return this.ticket?.status?.description ?? '';
  }

  get customer(): string {
    return this.ticket?.customer_name ?? 'Sem cliente';
  }

  get system(): string {
    return this.ticket?.system?.description ?? '';
  }

  get summary(): string {
    return this.ticket?.summary || this.ticket?.problem || '';
  }

  get tags(): string[] {
    return (this.ticket?.ticket_tags ?? [])
      .map(t => t.tag?.name)
      .filter((name): name is string => !!name);
  }

  get isUrgent(): boolean {
    return !!this.ticket?.urgent;
  }

  /** Mesmos critérios do card legado desta tela (`ticket-card-container`). */
  get isDefect(): boolean {
    const tipo =
      typeof this.ticket?.ticket_type === 'string'
        ? this.ticket.ticket_type
        : this.ticket?.ticket_type?.description;
    return tipo === 'Chamada Tipo Problema' || tipo === 'Chamada Defeito / Retrabalho';
  }

  get isPendingRelease(): boolean {
    return this.ticket?.type === 'L' || this.ticket?.release_situation === 'P';
  }

  get hasFailedTest(): boolean {
    return this.status === 'Teste não ok';
  }

  /** Copiada de `getStatusColor` do `sprint-board`. Casa por trecho: as descrições variam entre bases. */
  get statusColor(): string {
    const desc = this.status;
    if (!desc) return 'var(--v-bg-elevated, #f0f0f0)';
    const d = desc.toLowerCase();
    if (d.includes('teste ok') || d.includes('homologação')) return 'var(--p-green-500, #22c55e)';
    if (
      d.includes('teste não ok') ||
      d.includes('teste nao ok') ||
      d.includes('reprovado') ||
      d.includes('bloqueado')
    ) {
      return 'var(--p-red-500, #ef4444)';
    }
    if (d.includes('testar') || d.includes('teste')) return 'var(--p-yellow-400, #facc15)';
    if (d.includes('aguardando desenvolvimento')) return 'var(--p-surface-500, #64748b)';
    if (d.includes('andamento') || d.includes('desenvolvimento') || d.includes('revisão')) {
      return 'var(--p-blue-500, #3b82f6)';
    }
    if (d.includes('esforço definido')) return 'var(--p-surface-700, #334155)';
    return 'var(--p-surface-500, #64748b)';
  }
}
